<template>
  <header ref="tabs">
    <nav ref="nav" :class="{ '-sticky': sticky, '-stickyHeader': stickyHeader }">
      <div class="container" :class="{'text-center': item.declination === 'expertise'}">
        <ul
          ref="scroller"
          class="hide-scrollbar"
          @scroll="onUlScroll"
          @mousedown="dragStart"
          @mousemove="dragMove"
          @mouseup="dragEnd"
          @mouseleave="dragEnd"
        >
          <li v-for="(link, i) in items" :key="link.text" :class="{'preventClick': dragMoved}">
            <a v-if="item.declination === 'expertise'" :href="'#timeline-section-' + tableIndex[i]" @click.prevent="scrollTo('#timeline-section-' + tableIndex[i])" :class="{'-active': tableIndex[i] === currentDecade}" class="h5" draggable="false">{{ link.text }}</a>
            <a v-else :href="anchor ? anchor + link.anchor : '#timeline-section-' + link.anchor" @click.prevent="scrollTo(anchor ? anchor + link.anchor : '#timeline-section-' + link.anchor)" :class="{'-active': link.anchor === currentDecade}" class="h5" draggable="false">{{ link.text }}</a>
          </li>
        </ul>
        <div class="fade fade-left" v-show="canScrollLeft"></div>
        <div class="fade fade-right" v-show="canScrollRight"></div>
      </div>
    </nav>
  </header>
</template>

<script>
import { ReadyHook } from 'vue-asyncready'

export default {
  name: 'TimelineToolbar',
  props: ['item', 'items', 'siblingsComponents', 'pagePosition', 'anchor'],
  mixins: [ReadyHook],
  created() {
    if (typeof HTMLElement !== 'undefined') {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) {
          if (entries[0].boundingClientRect.y > entries[0].rootBounds.y) {
            this.currentDecade = this.tableIndex[this.tableIndex.findIndex(e => e === parseInt(entries[0].target.dataset.index)) - 1]
          }
        } else {
          this.currentDecade = parseInt(entries[0].target.dataset.index)
        }
      }, {
        threshold: 0.2,
        root: null,
        rootMargin: '0px 0px 0px 0px'
      })
    }
  },
  mounted() {
    window.addEventListener('scroll', this.headerPosition)
    this.tableIndex = this.siblingsComponents.map((component, index) => component.component === 'timeline_decade' ||
                                                                        component.component === 'podcast_expertise' ||
                                                                        component.component === 'generic_grid' ||
                                                                        component.component === 'category_block' ||
                                                                        component.component === 'movement_selector' ? index : null).filter(x => x !== null)
    setTimeout(() => {
      for (let i = 0; i < this.tableIndex.length; i++) {
        if (document.querySelector('#timeline-section-' + this.tableIndex[i])) {
          this.observer.observe(document.querySelector('#timeline-section-' + this.tableIndex[i]))
        } else if (this.anchor && document.querySelector(this.anchor + this.tableIndex[i])) {
          this.observer.observe(document.querySelector(this.anchor + this.tableIndex[i]))
        }
      }
    }, 100)
    window.addEventListener('resize', this.updateFadeVisibility)
    this.updateFadeVisibility()
    this.header = document.querySelector('.header-wrapper')
  },
  destroyed() {
    window.removeEventListener('scroll', this.headerPosition)
    window.removeEventListener('resize', this.updateFadeVisibility)
  },
  data() {
    return {
      sticky: false,
      stickyHeader: false,
      prevScrollY: 0,
      tableIndex: [],
      currentDecade: 1,
      header: null,
      canScrollLeft: false,
      canScrollRight: false,
      isDragging: false,
      dragStartX: 0,
      dragScrollLeft: 0,
      dragMoved: false
    }
  },
  computed: {},
  methods: {
    headerPosition() {
      if (!window) return

      const scrollY = window.pageYOffset || document.documentElement.scrollTop
      const rect = this.$refs.tabs.getBoundingClientRect()
      // element top relative to document
      const elemTop = rect.top + scrollY
      const headerStyle = window.getComputedStyle(this.header)
      const headerHeight = parseInt(headerStyle.height)

      // Become sticky once scrolled past the original element top
      if (scrollY > elemTop - headerHeight) {
        // set sticky fixed
        this.sticky = true
        this.$refs.nav.style.top = headerHeight + 'px'

        // determine scroll direction
        if (typeof this.prevScrollY === 'undefined') this.prevScrollY = scrollY
        if (scrollY > this.prevScrollY) {
          // scrolling down -> hide header
          this.stickyHeader = false
        } else {
          // scrolling up -> show header
          this.stickyHeader = true
        }
      } else {
        // not yet sticky
        this.sticky = false
        this.stickyHeader = false
        this.$refs.nav.style.top = 0
      }

      this.prevScrollY = scrollY
    },
    scrollTo(id) {
      if (window) {
        let bodyRect = document.body.getBoundingClientRect()
        let elemRect = document.querySelector(id).getBoundingClientRect()
        let offset = elemRect.top - bodyRect.top
        let decal = window.scrollY > offset ? 200 : 0
        window.scrollTo({
          top: offset - decal,
          behavior: 'smooth'
        })
      }
    },
    onUlScroll() {
      this.updateFadeVisibility()
    },
    updateFadeVisibility() {
      const el = this.$refs.scroller
      if (!el) {
        this.canScrollLeft = false
        this.canScrollRight = false
        return
      }
      const scrollLeft = Math.round(el.scrollLeft)
      const maxScrollLeft = Math.round(el.scrollWidth - el.clientWidth)

      this.canScrollLeft = scrollLeft > 0
      this.canScrollRight = scrollLeft < (maxScrollLeft - 1)
    },
    dragStart(e) {
      const el = this.$refs.scroller
      this.isDragging = true
      this.dragStartX = e.pageX - el.offsetLeft
      this.dragScrollLeft = el.scrollLeft
      this.dragMoved = false
    },
    dragMove(e) {
      if (!this.isDragging) return
      e.preventDefault()
      const el = this.$refs.scroller
      const x = e.pageX - el.offsetLeft
      const delta = x - this.dragStartX
      if (Math.abs(delta) > 0) this.dragMoved = true
      el.scrollLeft = this.dragScrollLeft - (x - this.dragStartX)
      this.updateFadeVisibility()
    },
    dragEnd() {
      this.isDragging = false
      this.dragMoved = false
    }
  },
  watch: {}
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

header {
  position: relative;
  height: 8.1rem;
  @include bp(max md) {
    height: 5.1rem;
  }
}

nav {
  position: absolute;
  z-index: 30;
  top: 0;
  width: 100%;
  background: var(--zenith-white);

  .container {
    position: relative;

    @include bp(max md) {
      padding: 0;
    }

    .fade{
      position: absolute;
      top: 0;
      height: 100%;
      background: linear-gradient(to right, transparent 0%, var(--zenith-app-background) 100%);
      width: 12rem;
      margin: 0 4rem;
      pointer-events: none;

      &-left{
        right: auto;
        left: 0;
        transform: rotate(180deg);
      }

      &-right{
        right: 0;
        left: auto;
      }

      @include bp(max md) {
        margin: 0;
      }
    }
  }

  &.-sticky {
    position: fixed;
    transition: transform 0.22s ease-in-out, box-shadow 0.2s ease-in-out;
    /* start hidden (moved up by its full height) */
    transform: translateY(-100%);
    left: 0;

    &:before {
      content: '';
      display: block;
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: .1rem;
    }

    ul:before {
      display: none;
    }

    &.-stickyHeader {
      /* show the header (slide down into view) */
      transform: translateY(0);
    }
  }

  ul {
    position: relative;
    margin: 0;
    padding: 0;
    list-style: none;
    overflow-x: auto;
    white-space: nowrap;
    text-align: center;
    display: flex;
    justify-content: space-evenly;

    user-select: none;

    @include bp(max md) {
      padding: 0 1.5rem;
      text-align: left;
    }

    li {
      padding: 0 3rem;
      display: inline-block;

      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        padding-right: 0;
      }

      &.preventClick {
        pointer-events: none;
      }

      a {
        display: block;
        padding: 3.4rem 0 0;
        height: 8rem;
        color: var(--zenith-theme-primary-lighter);
        text-transform: uppercase;
        transition: all 0.2s ease-in-out;
        border-bottom: 2px solid transparent;
        outline: none;

        @include bp(max md) {
          padding-top: 1.8rem;
          height: 5rem;
        }

        &:hover, &.-active {
          border-bottom: 2px solid var(--zenith-theme-primary);
        }
      }
    }
  }
}
</style>
